// src/components/common/Newsletter.js

import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { motion } from 'framer-motion';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ThemeContext } from '../context/ThemeContext';
import { newsletterService } from '../api';

const NewsletterContainer = styled.div`
  margin: ${props => props.condensed ? '1rem 0' : '3rem 0'};
  width: 100%;
`;

const NewsletterTitle = styled.h3`
  font-size: ${props => props.condensed ? '1.2rem' : '1.5rem'};
  margin-bottom: 1rem;
  color: ${props => props.theme.text};
`;

const NewsletterDescription = styled.p`
  margin-bottom: ${props => props.condensed ? '0.8rem' : '1.5rem'};
  color: ${props => props.theme.text};
  opacity: 0.8;
  font-size: ${props => props.condensed ? '0.9rem' : '1rem'};
`;

const NewsletterForm = styled.form`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  
  @media (max-width: ${props => props.condensed ? '400px' : '576px'}) {
    flex-direction: column;
  }
`;

const NewsletterInput = styled.input`
  flex: 1;
  min-width: ${props => props.condensed ? '150px' : '250px'};
  padding: ${props => props.condensed ? '0.6rem' : '0.8rem'};
  border: 1px solid ${props => props.theme.text}30;
  border-radius: 4px;
  background: ${props => props.theme.background};
  color: ${props => props.theme.text};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
  }
`;

const NewsletterButton = styled(motion.button)`
  padding: ${props => props.condensed ? '0.6rem 1rem' : '0.8rem 1.5rem'};
  background: ${props => props.theme.primary};
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 600;
  white-space: nowrap;
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled(motion.div)`
  margin-top: 1rem;
  padding: 0.8rem;
  background-color: ${props => props.theme.success};
  color: white;
  border-radius: 4px;
  text-align: center;
  font-size: ${props => props.condensed ? '0.9rem' : '1rem'};
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.error};
  font-size: ${props => props.condensed ? '0.8rem' : '0.9rem'};
  margin-top: 0.3rem;
  width: 100%;
`;

const Newsletter = ({ condensed = false }) => {
  const { theme } = useContext(ThemeContext);
  const { t } = useTranslation();
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
  
  // Validation schema
  const validationSchema = Yup.object({
    email: Yup.string().email(t('invalidEmail')).required(t('emailRequired')),
    name: Yup.string(),
  });
  
  // Formik configuration
  const formik = useFormik({
    initialValues: {
      email: '',
      name: '',
    },
    validationSchema,
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        // Appel à l'API pour s'inscrire à la newsletter
        await newsletterService.create(values);
        
        setSubscriptionSuccess(true);
        resetForm();
        
        // Masquer le message de succès après 5 secondes
        setTimeout(() => {
          setSubscriptionSuccess(false);
        }, 5000);
      } catch (error) {
        console.error("Error subscribing to newsletter:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });
  
  return (
    <NewsletterContainer condensed={condensed}>
      <NewsletterTitle theme={theme} condensed={condensed}>{t('newsletter')}</NewsletterTitle>
      <NewsletterDescription theme={theme} condensed={condensed}>
        {t('newsletterDescription')}
      </NewsletterDescription>
      
      <NewsletterForm onSubmit={formik.handleSubmit} condensed={condensed}>
        <NewsletterInput
          type="email"
          name="email"
          placeholder={t('emailPlaceholder')}
          theme={theme}
          condensed={condensed}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.email}
        />
        
        <NewsletterButton
          type="submit"
          theme={theme}
          condensed={condensed}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          disabled={formik.isSubmitting}
        >
          {formik.isSubmitting ? (
            <i className="fas fa-spinner fa-spin"></i>
          ) : (
            t('subscribe')
          )}
        </NewsletterButton>
        
        {formik.touched.email && formik.errors.email && (
          <ErrorMessage theme={theme} condensed={condensed}>{formik.errors.email}</ErrorMessage>
        )}
      </NewsletterForm>
      
      {subscriptionSuccess && (
        <SuccessMessage
          theme={theme}
          condensed={condensed}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {t('subscriptionSuccess')}
        </SuccessMessage>
      )}
    </NewsletterContainer>
  );
};

export default Newsletter;